/**
 * Enthält die komplette Spiel-Initialisierung.
 * Extrahiert aus main.ts für den neuen Auth-Flow.
 */
import { Engine } from './Engine';
import { Camera } from '../renderer/Camera';
import { RenderPipeline } from '../renderer/RenderPipeline';
import { CameraController } from '../input/CameraController';
import { HexPicker } from '../input/HexPicker';
import { WorldManager } from '../world/WorldManager';
import { createWorldInfo } from '../config/WorldConfig';
import { defaultCameraConfig } from '../config/CameraConfig';
import { defaultStellarRenderConfig } from '../config/StellarRenderConfig';
import { defaultFogConfig } from '../config/FogConfig';
import { defaultTravelConfig } from '../config/TravelConfig';
import { axialToWorld } from '../math/HexMath';
import { TravelLineRenderer } from '../objects/TravelLineRenderer';
import { FleetRenderer } from '../objects/FleetRenderer';
import { TravelSystem } from '../systems/TravelSystem';
import { TravelPhase } from '../systems/TravelTypes';
import { DebugHUD } from '../ui/DebugHUD';
import { GameStateHUD } from '../ui/GameStateHUD';
import { StatsBridge } from '../systems/StatsBridge';
import { TravelPanel } from '../ui/TravelPanel';
import { TravelSettingsPanel } from '../ui/TravelSettingsPanel';
import { defaultFleetConfig } from '../config/FleetConfig';
import { FleetState } from '../systems/FleetState';
import { FleetIndicator } from '../objects/FleetIndicator';
import { ShipPreviewPanel } from '../ui/ShipPreviewPanel';
import { DebugCircleRenderer } from '../objects/DebugCircleRenderer';
import { SunLifecycle } from '../systems/SunLifecycle';
import { defaultSunLifecycleConfig } from '../config/SunLifecycleConfig';
import { SunTimerOverlay } from '../ui/SunTimerOverlay';
import { SunTimerBillboard } from '../objects/SunTimerBillboard';
import { setupSupernovaEffects } from '../systems/SupernovaSetup';
import { PlayerBadge } from '../ui/PlayerBadge';
import { AccountPanel } from '../ui/AccountPanel';
import { setupPlayerState } from './PlayerBootSetup';
import { TravelStressTest } from '../systems/TravelStressTest';
import { StressTestHUD } from '../ui/StressTestHUD';
import type { SupabaseClient } from '../network/SupabaseClient';
import type { LoadingScreen } from '../ui/LoadingScreen';
import type { PlanetSummary } from '../world/PlayerWorldTypes';
import type { AuthUser } from '../network/AuthTypes';
import { injectResponsiveVars } from '../ui/UITheme';
import { TravelButton } from '../ui/TravelButton';
import { TravelMenu } from '../ui/TravelMenu';
import { FleetWorldMarkers } from '../objects/FleetWorldMarkers';

export async function bootGame(
  supabase: SupabaseClient,
  loading: LoadingScreen,
  authUser: AuthUser | null,
  onLogout: (() => void) | null,
  startPlanet?: PlanetSummary | null,
): Promise<void> {
  const playerId = authUser?.playerId;
  const playerName = authUser?.displayName || authUser?.accountName || 'uneingeloggt';
  injectResponsiveVars();
  window.addEventListener('resize', injectResponsiveVars);

  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const engine = new Engine(canvas);

  loading.setProgress(30, 'datenbank...');
  console.log('[BOOT] start', import.meta.env.VITE_SUPABASE_URL);

  const { data: worldRow, error: worldErr } = await supabase
    .from('worlds')
    .select('*')
    .in('state', ['generated', 'active'])
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (worldErr || !worldRow) {
    loading.showError('keine spielwelt gefunden');
    return;
  }

  const worldInfo = createWorldInfo({
    hexRadius: Number(worldRow.hex_radius),
    clusterWidth: worldRow.cluster_width,
    clusterHeight: worldRow.cluster_height,
    worldCols: worldRow.world_cols,
    worldRows: worldRow.world_rows,
    maxActiveClusters: 32,
    seed: worldRow.seed,
  });

  loading.setProgress(50, 'renderer...');
  const camConfig = defaultCameraConfig();
  const stellarConfig = defaultStellarRenderConfig();
  const fogConfig = defaultFogConfig();

  const startQ = startPlanet ? startPlanet.axialQ : Math.floor(worldInfo.worldWidthHexes / 2);
  const startR = startPlanet ? startPlanet.axialR : 0;
  const [startX, startZ] = axialToWorld(startQ, startR, worldInfo.config.hexRadius);

  const camera = new Camera(camConfig);
  camera.target.set(startX, 0, startZ);
  const cameraCtrl = new CameraController(camera, camConfig, worldInfo.config.hexRadius);
  const pipeline = new RenderPipeline(camera);

  loading.setProgress(70, 'welt...');
  const world = new WorldManager(
    camera, pipeline, engine.gl, worldInfo,
    camConfig.flightView.transitionSpeed,
    supabase, worldRow.id, stellarConfig, fogConfig,
  );
  await setupPlayerState(
    engine, supabase, world, camera,
    worldInfo.config.hexRadius, worldRow.id,
    playerId, startQ, startR,
  );

  const travelConfig = defaultTravelConfig();
  const fleetConfig = defaultFleetConfig();
  const fleetState = new FleetState(fleetConfig);
  const travelLineRenderer = new TravelLineRenderer(engine.gl, travelConfig);
  const fleetRenderer = new FleetRenderer(
    engine.gl, fleetState, fleetConfig.formationSpacing, worldInfo.config.hexRadius,
  );
  const fleetIndicator = new FleetIndicator(fleetState, fleetConfig);
  pipeline.addRenderable(travelLineRenderer);
  pipeline.addRenderable(fleetRenderer);
  pipeline.addRenderable(fleetIndicator);

  try { await fleetRenderer.loadModels(); }
  catch (e) { console.warn('[BOOT] Fleet models not found, using placeholder:', e); }

  const cm = world.contentManager;
  const sunKeysProvider = cm ? () => cm!.sunHexKeys : null;
  const planetKeysProvider = cm ? () => cm!.planetHexKeys : null;
  const travelSystem = new TravelSystem(
    travelConfig, worldInfo, world.fogState ?? null,
    travelLineRenderer, fleetRenderer, sunKeysProvider, planetKeysProvider,
    fleetState, fleetIndicator,
  );
  const devMode = !playerId;
  const hexPicker = new HexPicker(camera, worldInfo.config.hexRadius);
  const travelPanel = devMode ? new TravelPanel() : null;
  const travelSettings = devMode ? new TravelSettingsPanel(travelConfig, fleetConfig) : null;

  engine.events.on<{ phase: number }>('travel:phase-changed', (d) => {
    hexPicker.enabled = d.phase === TravelPhase.SelectingStart || d.phase === TravelPhase.SelectingEnd;
  });
  engine.events.on<{ enabled: boolean }>('hex-picker:set-enabled', (d) => {
    hexPicker.enabled = d.enabled;
  });
  engine.events.on<null>('fleet:composition-changed', () => fleetState.setComposition(fleetConfig.composition));
  cameraCtrl.setFollowSource(fleetState);

  const debugHud = new DebugHUD(devMode);
  const gameStateHud = new GameStateHUD(devMode);
  Object.assign(gameStateHud.state, { dbStatus: 'OK', worldName: worldRow.name, playerName });
  const statsBridge = new StatsBridge(debugHud.stats, gameStateHud.state, pipeline, world, worldInfo);
  const sunLifecycle = new SunLifecycle(defaultSunLifecycleConfig(), new Date(worldRow.created_at).getTime());

  loading.setProgress(85, 'module...');
  engine.register(sunLifecycle);
  setupSupernovaEffects(engine, pipeline, worldInfo.config.hexRadius);
  for (const m of [cameraCtrl, camera, hexPicker, world, travelSystem, pipeline]) engine.register(m);
  if (travelPanel) engine.register(travelPanel);
  if (travelSettings) engine.register(travelSettings);
  engine.register(fleetIndicator); fleetIndicator.setCamera(camera);
  engine.register(fleetRenderer);

  const debugCircle = new DebugCircleRenderer(engine.gl);
  pipeline.addRenderable(debugCircle); fleetRenderer.setDebugCircle(debugCircle);
  engine.register(new ShipPreviewPanel(camera, fleetState, fleetRenderer, debugCircle));
  const sunTimerBillboard = new SunTimerBillboard(engine.gl);
  pipeline.addRenderable(sunTimerBillboard);
  const sunTimerOverlay = new SunTimerOverlay(sunTimerBillboard, worldInfo.config.hexRadius, stellarConfig.sunBaseSize);
  engine.register(sunTimerOverlay);
  engine.register(statsBridge); engine.register(debugHud); engine.register(gameStateHud);

  if (!playerId) {
    const stressTest = new TravelStressTest(
      camera, pipeline, worldInfo, travelConfig, fleetConfig, sunKeysProvider,
    );
    engine.register(stressTest);
    statsBridge.setStressTest(stressTest);
    const stressTestHud = new StressTestHUD(stressTest);
    engine.register(stressTestHud);
  }

  if (!devMode && authUser) {
    let avatarId = 0;
    const { data: pRow } = await supabase
      .from('players').select('avatar_id').eq('id', authUser.playerId).single();
    if (pRow) avatarId = pRow.avatar_id ?? 0;
    engine.register(new PlayerBadge(playerName, onLogout, 1, avatarId));
    engine.register(new AccountPanel(supabase, {
      email: authUser.email, displayName: authUser.displayName,
      accountName: authUser.accountName, playerId: authUser.playerId, avatarId,
    }));
  }

  // Reise-System: Button + Menü (auch im Dev-Modus)
  engine.register(new TravelButton());
  const travelPlayerId = authUser?.playerId ?? 'dev';
  engine.register(new TravelMenu(supabase, worldRow.id, travelPlayerId, travelConfig, worldInfo, sunKeysProvider));
  const fleetWorldMarkers = new FleetWorldMarkers(
    engine.gl, worldInfo.config.hexRadius, travelConfig, planetKeysProvider,
  );
  try { await fleetWorldMarkers.loadModels(); }
  catch (e) { console.warn('[BOOT] FleetWorldMarkers models not loaded:', e); }
  pipeline.addRenderable(fleetWorldMarkers);
  engine.register(fleetWorldMarkers);
  // Im Live-Modus: FleetWorldMarkers' followProxy als Follow-Source für FlightView
  if (!devMode) cameraCtrl.setFollowSource(fleetWorldMarkers.followProxy);

  loading.setProgress(100, 'bereit');
  engine.start();
  setTimeout(() => loading.hide(), 300);
}
